import { Fragment, type ReactElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  resolveCoverageMetricThresholdColor,
  type CoverageMetricThresholdKey,
} from '@/features/codeCoverage/pages/coverageMetricThresholdColors';
import {
  DAILY_STATUS_BLOCK_SIZE,
  DAILY_STATUS_ROW_HEIGHT,
} from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel.constants';
import { r, s } from '@/theme/tokens';
import { uiTokens } from '@/theme/uiTokens';
import {
  Box,
  darken,
  IconButton,
  Link,
  Skeleton,
  TableCell,
  TableRow,
  Text,
} from '@/ui/primitives';
import {
  KeyboardArrowDownOutlinedIcon,
  KeyboardArrowRightOutlinedIcon,
} from '@/ui/primitives/icons';
import type { CoverageMetricThresholdGroup, MetricThreshold } from '@flowqa/shared';

export type CoverageMetrics = {
  branchesPct: number | null;
  functionsPct: number | null;
  linesPct: number;
  statementsPct: number | null;
};

export type CoverageHistoryTreeNode = CoverageMetrics & {
  children: CoverageHistoryTreeNode[];
  fileId: string | null;
  hasChildren: boolean;
  id: string;
  isLoadingChildren?: boolean;
  name: string;
  path: string;
  type: 'directory' | 'file';
};

export type CoverageHistoryGroupRow = CoverageMetrics & {
  children: CoverageHistoryTreeNode[];
  hasChildren: boolean;
  id: string;
  isLoadingChildren?: boolean;
  name: string;
  path?: string;
};

const isHistoryTreeNode = (
  row: CoverageHistoryGroupRow | CoverageHistoryTreeNode,
): row is CoverageHistoryTreeNode => 'type' in row;

type CoverageHistoryRowsProps = {
  ancestorHasNextSiblings: boolean[];
  directoryThresholds: CoverageMetricThresholdGroup;
  directoryHrefByPath: ReadonlyMap<string, string>;
  depth: number;
  expandedRowIds: Set<string>;
  fileThresholds: CoverageMetricThresholdGroup;
  onToggleRow: (rowId: string) => void;
  rows: Array<CoverageHistoryGroupRow | CoverageHistoryTreeNode>;
};

type VisibleCoverageHistoryRow = {
  ancestorHasNextSiblings: boolean[];
  depth: number;
  isLastSibling: boolean;
  row: CoverageHistoryGroupRow | CoverageHistoryTreeNode;
};

const BODY_CELL_SX = {
  px: s('md'),
  py: s('none'),
  borderBottom: 'none',
  minWidth: 0,
  verticalAlign: 'middle',
} as const;
const LOADING_BODY_CELL_SX = {
  pt: s('md'),
  verticalAlign: 'top',
} as const;

const TREE_TOGGLE_SIZE = 32;
const TREE_ICON_SIZE = 26;
const LOADING_ROW_COUNT = 2;

const CoverageLoadingSkeletonRows = ({
  ancestorHasNextSiblings,
  depth,
  rowId,
}: {
  ancestorHasNextSiblings: boolean[];
  depth: number;
  rowId: string;
}): ReactElement => {
  const connectorPrefix = Array.from({ length: depth + 1 }, (_unused, index) => {
    const isCurrentDepth = index === depth;
    if (!isCurrentDepth) {
      return ancestorHasNextSiblings[index] ? '│ ' : '  ';
    }

    return '├ ';
  }).join('');

  return (
    <>
      {Array.from({ length: LOADING_ROW_COUNT }, (_unused, index) => (
        <TableRow
          key={`${rowId}-loading-${String(index)}`}
          sx={{ height: `${DAILY_STATUS_ROW_HEIGHT}px` }}
        >
          <TableCell sx={[BODY_CELL_SX, LOADING_BODY_CELL_SX]}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                minHeight: '32px',
                minWidth: 0,
                width: '100%',
              }}
            >
              <Text
                role="body"
                sx={{
                  fontFamily: (theme) => theme.typography.mono.fontFamily,
                  color: 'text.secondary',
                  whiteSpace: 'pre',
                  flexShrink: 0,
                  mr: '2px',
                }}
              >
                {connectorPrefix}
              </Text>
              <Skeleton
                variant="rounded"
                width={index % 2 === 0 ? '68%' : '61%'}
                sx={{
                  height: '14px',
                  transform: 'none',
                  borderRadius: r('pill'),
                }}
              />
              <Box
                sx={{
                  ml: s('md'),
                  width: TREE_TOGGLE_SIZE,
                  height: TREE_TOGGLE_SIZE,
                  flexShrink: 0,
                }}
              />
            </Box>
          </TableCell>
          {[0, 1, 2, 3].map((columnIndex) => (
            <TableCell
              key={`${rowId}-loading-${String(index)}-${String(columnIndex)}`}
              sx={[BODY_CELL_SX, LOADING_BODY_CELL_SX]}
            >
              <Skeleton
                variant="rounded"
                sx={{
                  width: '100%',
                  height: '14px',
                  transform: 'none',
                  borderRadius: r('sm'),
                }}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
};

const buildVisibleRows = ({
  ancestorHasNextSiblings,
  directoryThresholds: _directoryThresholds,
  directoryHrefByPath: _directoryHrefByPath,
  depth,
  expandedRowIds,
  fileThresholds: _fileThresholds,
  rows,
}: Omit<CoverageHistoryRowsProps, 'onToggleRow'>): VisibleCoverageHistoryRow[] =>
  rows.flatMap((row, index) => {
    const isLastSibling = index === rows.length - 1;
    const visibleRow: VisibleCoverageHistoryRow = {
      ancestorHasNextSiblings,
      depth,
      isLastSibling,
      row,
    };

    if (!row.hasChildren || !expandedRowIds.has(row.id)) {
      return [visibleRow];
    }

    const nextAncestorHasNextSiblings =
      depth === 0 ? ancestorHasNextSiblings : [...ancestorHasNextSiblings, !isLastSibling];

    return [
      visibleRow,
      ...buildVisibleRows({
        ancestorHasNextSiblings: nextAncestorHasNextSiblings,
        directoryThresholds: _directoryThresholds,
        directoryHrefByPath: _directoryHrefByPath,
        depth: depth + 1,
        expandedRowIds,
        fileThresholds: _fileThresholds,
        rows: row.children,
      }),
    ];
  });

const formatCoveragePct = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return '-';
  }

  const roundedValue = Math.round(value * 100) / 100;
  return `${roundedValue.toLocaleString(undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  })}%`;
};

const resolveCoverageBarColor = (
  value: number | null | undefined,
  threshold: MetricThreshold,
): string => {
  if (value === null || value === undefined) {
    return 'transparent';
  }

  const tone = resolveCoverageMetricThresholdColor(value, threshold);

  if (tone === 'success') {
    return darken(uiTokens.statusColors.green, 0.26);
  }
  if (tone === 'warning') {
    return darken(uiTokens.statusColors.orange, 0.26);
  }
  if (tone === 'error') {
    return darken(uiTokens.statusColors.red, 0.26);
  }

  return darken(uiTokens.statusColors.neutral, 0.22);
};

const CoverageMetricValueCell = ({
  threshold,
  value,
}: {
  threshold: MetricThreshold;
  value: number | null | undefined;
}): ReactElement => {
  const safeValue =
    value === null || value === undefined ? null : Math.max(0, Math.min(value, 100));

  return (
    <Box
      sx={{
        width: '100%',
        height: `${DAILY_STATUS_BLOCK_SIZE}px`,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: r('sm'),
        overflow: 'hidden',
        backgroundColor: 'background.paper',
        position: 'relative',
      }}
    >
      {safeValue !== null ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            width: `${String(safeValue)}%`,
            backgroundColor: resolveCoverageBarColor(safeValue, threshold),
          }}
        />
      ) : null}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: s('sm'),
        }}
      >
        <Text
          role="body"
          sx={{
            fontSize: (theme) => theme.typography.pxToRem(13),
            lineHeight: 1,
          }}
        >
          {formatCoveragePct(value)}
        </Text>
      </Box>
    </Box>
  );
};

const TreeNameCell = ({
  ancestorHasNextSiblings,
  depth,
  href,
  isDirectory,
  isExpanded,
  isLastSibling,
  name,
  onToggle,
  showExpand,
}: {
  ancestorHasNextSiblings: boolean[];
  depth: number;
  href?: string;
  isDirectory: boolean;
  isExpanded: boolean;
  isLastSibling: boolean;
  name: string;
  onToggle?: () => void;
  showExpand: boolean;
}): ReactElement => {
  const connectorPrefix = Array.from({ length: depth }, (_unused, index) => {
    const isCurrentDepth = index === depth - 1;
    if (!isCurrentDepth) {
      return ancestorHasNextSiblings[index] ? '│ ' : '  ';
    }
    return isLastSibling ? '└ ' : '├ ';
  }).join('');

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: '32px',
        minWidth: 0,
        width: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
        {depth > 0 ? (
          <Text
            role="body"
            sx={{
              fontFamily: (theme) => theme.typography.mono.fontFamily,
              color: 'text.secondary',
              whiteSpace: 'pre',
              flexShrink: 0,
              mr: '2px',
            }}
          >
            {connectorPrefix}
          </Text>
        ) : null}
        {href ? (
          <Link
            component={RouterLink}
            to={href}
            underline="none"
            sx={{
              fontFamily: (theme) => theme.typography.mono.fontFamily,
              fontSize: (theme) => theme.typography.pxToRem(15),
              color: isDirectory && depth > 0 ? 'text.secondary' : 'common.white',
              display: 'inline-block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {name}
          </Link>
        ) : (
          <Text
            role="body"
            sx={{
              fontFamily: (theme) => theme.typography.mono.fontFamily,
              fontSize: (theme) => theme.typography.pxToRem(15),
              color: isDirectory && depth > 0 ? 'text.secondary' : 'common.white',
              display: 'inline-block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
            }}
          >
            {name}
          </Text>
        )}
      </Box>
      {showExpand ? (
        <IconButton
          aria-label={isExpanded ? `Collapse ${name}` : `Expand ${name}`}
          onClick={(event) => {
            event.stopPropagation();
            onToggle?.();
          }}
          sx={{
            color: 'text.secondary',
            ml: s('md'),
            flexShrink: 0,
            width: TREE_TOGGLE_SIZE,
            height: TREE_TOGGLE_SIZE,
            p: s('xxs'),
          }}
        >
          {isExpanded ? (
            <KeyboardArrowDownOutlinedIcon
              sx={{
                fontSize: (theme) => theme.typography.pxToRem(TREE_ICON_SIZE),
                width: `${TREE_ICON_SIZE}px`,
                height: `${TREE_ICON_SIZE}px`,
              }}
            />
          ) : (
            <KeyboardArrowRightOutlinedIcon
              sx={{
                fontSize: (theme) => theme.typography.pxToRem(TREE_ICON_SIZE),
                width: `${TREE_ICON_SIZE}px`,
                height: `${TREE_ICON_SIZE}px`,
              }}
            />
          )}
        </IconButton>
      ) : null}
    </Box>
  );
};

export const CoverageRunsHistoryRows = ({
  ancestorHasNextSiblings,
  directoryThresholds,
  directoryHrefByPath,
  depth,
  expandedRowIds,
  fileThresholds,
  onToggleRow,
  rows,
}: CoverageHistoryRowsProps): ReactElement => {
  const visibleRows = buildVisibleRows({
    ancestorHasNextSiblings,
    directoryThresholds,
    directoryHrefByPath,
    depth,
    expandedRowIds,
    fileThresholds,
    rows,
  });

  return (
    <>
      {visibleRows.map(
        ({ ancestorHasNextSiblings: rowAncestors, depth: rowDepth, isLastSibling, row }) => {
          const hasChildren = row.hasChildren;
          const isExpanded = expandedRowIds.has(row.id);
          const href = isHistoryTreeNode(row)
            ? row.type === 'file'
              ? row.fileId
                ? `/coverage-files/${row.fileId}`
                : undefined
              : directoryHrefByPath.get(row.path)
            : row.path
              ? directoryHrefByPath.get(row.path)
              : undefined;
          const thresholdGroup =
            isHistoryTreeNode(row) && row.type === 'file' ? fileThresholds : directoryThresholds;
          const resolveThreshold = (metricKey: CoverageMetricThresholdKey): MetricThreshold =>
            thresholdGroup[metricKey];
          return (
            <Fragment key={row.id}>
              <TableRow sx={{ height: `${DAILY_STATUS_ROW_HEIGHT}px` }}>
                <TableCell sx={[BODY_CELL_SX, { verticalAlign: 'middle' }]}>
                  <TreeNameCell
                    ancestorHasNextSiblings={rowAncestors}
                    depth={rowDepth}
                    href={href}
                    isDirectory={isHistoryTreeNode(row) ? row.type !== 'file' : true}
                    isExpanded={isExpanded}
                    isLastSibling={isLastSibling}
                    name={row.name}
                    onToggle={
                      hasChildren
                        ? () => {
                            onToggleRow(row.id);
                          }
                        : undefined
                    }
                    showExpand={hasChildren}
                  />
                </TableCell>
                <TableCell sx={BODY_CELL_SX}>
                  <CoverageMetricValueCell
                    threshold={resolveThreshold('line')}
                    value={row.linesPct}
                  />
                </TableCell>
                <TableCell sx={BODY_CELL_SX}>
                  <CoverageMetricValueCell
                    threshold={resolveThreshold('function')}
                    value={row.functionsPct}
                  />
                </TableCell>
                <TableCell sx={BODY_CELL_SX}>
                  <CoverageMetricValueCell
                    threshold={resolveThreshold('branch')}
                    value={row.branchesPct}
                  />
                </TableCell>
                <TableCell sx={BODY_CELL_SX}>
                  <CoverageMetricValueCell
                    threshold={resolveThreshold('statement')}
                    value={row.statementsPct}
                  />
                </TableCell>
              </TableRow>
              {isExpanded && row.isLoadingChildren ? (
                <CoverageLoadingSkeletonRows
                  ancestorHasNextSiblings={rowAncestors}
                  depth={rowDepth}
                  rowId={row.id}
                />
              ) : null}
            </Fragment>
          );
        },
      )}
    </>
  );
};
